import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="catalog--replacement-for"
// Handles the "Replacement for" field interaction in the Add Part form:
// - When a parent part is selected, qty is set to 0 and disabled
// - When "none" is selected, qty is re-enabled
// - Shows/hides a contextual hint
// - When activeRevision is true, replacement selection is mandatory
// - In edit mode (CONEK-5272, BoM line edit form) an existing replacement line
//   keeps its server-rendered qty, and the qty is locked with readOnly instead
//   of disabled so the 0 is still submitted
export default class extends Controller {
  static targets = ["select", "qtyInput", "hint"]
  static values = {
    activeRevision: { type: Boolean, default: false },
    edit: { type: Boolean, default: false },
  }

  connect() {
    this._originalQty = this.qtyInputTarget.value || ""
    // Edit mode: an existing replacement line keeps its editable qty on load.
    this._wasReplacement = this.editValue && this.selectTarget.value !== ""
    this._update()
  }

  changed() {
    this._update()
  }

  _update() {
    const isReplacement = this.selectTarget.value !== ""
    const selectedOption = this.selectTarget.selectedOptions[0]
    const becameReplacement = isReplacement && !this._wasReplacement
    this._wasReplacement = isReplacement

    if (isReplacement) {
      // Zero the qty only on the None→parent transition, so re-pointing a
      // replacement to another parent keeps its qty (CONEK-5272).
      if (becameReplacement) {
        this._originalQty = this.qtyInputTarget.value
        this.qtyInputTarget.value = "0"
        this._lockQty(true)
        this.qtyInputTarget.removeAttribute("required")
        this.qtyInputTarget.removeAttribute("min")
      }

      if (this.hasHintTarget) {
        // Ui::SelectComponent (Tom Select) doesn't emit a per-option data-code,
        // so derive the code from the "CODE — name" label, with a data-code
        // fallback for any caller that still sets it.
        const code = selectedOption
          ? selectedOption.dataset.code ||
            selectedOption.text.split("—")[0].trim()
          : ""
        this.hintTarget.textContent =
          this.hintTarget.dataset.templateValue.replace("{CODE}", code)
        this.hintTarget.classList.remove("hidden")
      }

      // Clear any validation error
      this.selectTarget.setCustomValidity("")
    } else {
      this._lockQty(false)
      this.qtyInputTarget.setAttribute("required", "")
      this.qtyInputTarget.setAttribute("min", "0")
      if (
        this.qtyInputTarget.value === "0" ||
        this.qtyInputTarget.value === "0.0000"
      ) {
        this.qtyInputTarget.value = this._originalQty || ""
      }

      if (this.hasHintTarget) {
        this.hintTarget.classList.add("hidden")
      }

      // When on active revision, replacement is mandatory
      if (this.activeRevisionValue) {
        this.selectTarget.setCustomValidity(
          "Only replacement parts can be added to an active revision. Please select a 'Replacement for' value.",
        )
      }
    }
  }

  _lockQty(locked) {
    if (this.editValue) {
      this.qtyInputTarget.readOnly = locked
    } else {
      this.qtyInputTarget.disabled = locked
    }
  }
}
